import { useState, useMemo, useRef, useEffect } from 'react'
import Tippy from '@tippyjs/react'
import useLocalStorageState from 'use-local-storage-state'
import classNames from 'classnames'
import styles from './ColumnChooserPopover.module.css'


function SectionHead({ children, action }) {
  return (
    <div className='flex items-center justify-between gap-2 px-3 pt-3 pb-2'>
      <span className='text-[10px] font-semibold uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500'>
        {children}
      </span>
      {action}
    </div>
  )
}

// columns: Array von [id, label] -- VOLLSTAENDIG fuer das jeweilige Modul,
// weil das Popover beim Sortieren die komplette Order schreibt (fehlende IDs
// wuerden vom Grid-Reconcile ans Ende sortiert). columns=null/leer blendet die
// Spalten-Sektion aus (z.B. Card View: dort gibt es nichts zu konfigurieren).
//
// viewOptions (optional): { value, onChange, options: [{ value, label }] } --
// rendert einen Ansicht-Umschalter (Segmented Control) ueber den Spalten.
export default function ColumnChooserPopover({
  application,
  columns,
  onReset,
  viewOptions,
  children,
}) {
  let [isOpen, setIsOpen] = useState(false)
  const hasColumns = columns?.length > 0
  const tippyRef = useRef(null)

  // Inhaltswechsel (Multiview <-> Card View: Spalten-Sektion erscheint/
  // verschwindet) aendert die Popover-Hoehe -> Popper muss neu positionieren,
  // sonst behaelt die Box ihre alte Hoehe und das Menue "springt" weg.
  useEffect(() => {
    tippyRef.current?.popperInstance?.update?.()
  }, [hasColumns, viewOptions?.value])

  return <Tippy
    onCreate={(instance) => { tippyRef.current = instance }}
    visible={isOpen}
    onClickOutside={() => setIsOpen(false)}
    placement='bottom-end'
    interactive={true}
    arrow={false}
    // Ins document.body portalen statt in den Trigger-Parent: der Toolbar-Header
    // und die Pagination-Bar sind beide sticky mit z-20 und bilden je einen
    // eigenen Stacking-Context. Inline gerendert bliebe das Popover im z-20 des
    // Headers gefangen -> die (spaeter im DOM stehende) z-20-Footerbar malt
    // darueber, sobald die Seite leer ist und die Bar hochrutscht. Im body liegt
    // es bei Tippys Default-z-index (9999) ueber allem. (.dark sitzt auf <html>,
    // bleibt also auch hier wirksam.)
    appendTo={() => document.body}
    className={styles.popover}
    content={
      // Ohne Spalten-Sektion (Card View) endet der Inhalt direkt unterm
      // Ansicht-Segment. Padding-Bottom am Container gibt sichtbare Luft --
      // margin am Segment wuerde aus der (padding-/border-losen) Box
      // herauskollabieren und nichts bewirken.
      <div className={classNames(!hasColumns && 'pb-2')}>
        <header>
          <h5 className='font-bold'>
            <span className='text-primary'>{viewOptions ? 'Ansicht' : 'Spalten'}</span>
            {viewOptions ? ' & Spalten' : ''}
          </h5>
        </header>

        {viewOptions && (
          <>
            <SectionHead>Ansicht</SectionHead>
            <div
              className='mx-3 grid grid-flow-col gap-1 rounded-lg bg-neutral-100 dark:bg-neutral-800 p-1'
            >
              {viewOptions.options.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  className={classNames(
                    'px-2 py-2 rounded-md text-xs transition-colors',
                    option.value === viewOptions.value
                      ? 'bg-white dark:bg-neutral-700 text-primary font-semibold shadow'
                      : 'font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                  )}
                  onClick={() => viewOptions.onChange(option.value)}
                >{option.label}</button>
              ))}
            </div>
          </>
        )}

        {hasColumns && (
          // key=application: beim View-Wechsel (z.B. Table <-> Multiview im
          // Wholesale) setzen Storage-Hooks/Suche/Drag-State sauber neu auf,
          // waehrend das Popover selbst offen bleibt.
          <ColumnSection key={application} application={application} columns={columns} onReset={onReset} />
        )}
      </div>
    }
  >
    {children({ setIsOpen, isOpen })}
  </Tippy>
}


function ColumnSection({ application, columns, onReset }) {
  let [search, setSearch] = useState('')
  // Reset nur nach Inline-Bestaetigung ("Wirklich? Ja/Nein") -- faellt nach
  // ein paar Sekunden ohne Klick automatisch zurueck.
  let [confirmReset, setConfirmReset] = useState(false)
  useEffect(() => {
    if (!confirmReset) return
    const timer = setTimeout(() => setConfirmReset(false), 4000)
    return () => clearTimeout(timer)
  }, [confirmReset])
  let [hiddenColumnIds, setHiddenColumnIds] = useLocalStorageState(`${application}-col-hidden`, {
    defaultValue: []
  })
  // Gleicher Storage-Key wie columnOrder in ProductGrid/ProductTable --
  // use-local-storage-state synct Hooks auf demselben Key, das Grid uebernimmt
  // die neue Reihenfolge live (und der Server-Sync dort laeuft mit).
  let [columnOrder, setColumnOrder] = useLocalStorageState(`${application}-col-order`)

  // Drag & Drop zum Sortieren (Logik aus dem Product-Grid-Re-Design-Mock)
  let [dragId, setDragId] = useState(null)
  let [overId, setOverId] = useState(null)
  const listRef = useRef(null)
  const dragYRef = useRef(null)
  const rafRef = useRef(null)
  useEffect(() => () => cancelAnimationFrame(rafRef.current), [])

  // Anzeige in der aktuell wirksamen Grid-Reihenfolge (gleiche Reconcile-
  // Semantik wie im Grid: gespeicherte Order zuerst, unbekannte IDs hinten dran)
  const orderedColumns = useMemo(() => {
    const byId = new Map(columns.map((col) => [col[0], col]))
    const result = []
    if (Array.isArray(columnOrder)) {
      for (const id of columnOrder) {
        const col = byId.get(id)
        if (col) {
          result.push(col)
          byId.delete(id)
        }
      }
    }
    for (const col of columns) {
      if (byId.has(col[0])) result.push(col)
    }
    return result
  }, [columns, columnOrder])

  const filteredColumns = useMemo(() => {
    if (!search.trim()) return orderedColumns
    const q = search.toLowerCase()
    return orderedColumns.filter(([, title]) => title.toLowerCase().includes(q))
  }, [orderedColumns, search])

  // Verschieben relativ zur Ziel-ID (funktioniert dadurch auch bei aktiver
  // Suche). Basis ist die gespeicherte VOLLE Order, damit IDs, die dieses
  // Popover nicht kennt, ihre Position behalten.
  const reorder = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return
    const base = Array.isArray(columnOrder) && columnOrder.length
      ? [...columnOrder]
      : orderedColumns.map(([id]) => id)
    for (const [id] of orderedColumns) {
      if (!base.includes(id)) base.push(id)
    }
    const next = base.filter((id) => id !== sourceId)
    const at = targetId === '__end' ? next.length : next.indexOf(targetId)
    if (at === -1) return
    next.splice(at, 0, sourceId)
    setColumnOrder(next)
  }

  // Auto-Scroll am Listenrand waehrend des Ziehens -- so kommt man in einem
  // Zug von ganz oben nach ganz unten.
  const startAutoScroll = () => {
    cancelAnimationFrame(rafRef.current)
    const step = () => {
      const list = listRef.current
      if (list && dragYRef.current != null) {
        const rect = list.getBoundingClientRect()
        const zone = 40
        const y = dragYRef.current
        if (y < rect.top + zone) {
          list.scrollTop -= Math.min(14, Math.ceil((rect.top + zone - y) / 3))
        } else if (y > rect.bottom - zone) {
          list.scrollTop += Math.min(14, Math.ceil((y - (rect.bottom - zone)) / 3))
        }
      }
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }

  const endDrag = () => {
    cancelAnimationFrame(rafRef.current)
    dragYRef.current = null
    setDragId(null)
    setOverId(null)
  }

  return (
    <>
      <SectionHead
        action={onReset && (confirmReset ? (
          <span className='flex items-center gap-2'>
            <span className='text-xs font-medium text-neutral-400 dark:text-neutral-500 normal-case'>Wirklich?</span>
            <button
              className='text-xs font-semibold text-red-500 hover:underline'
              onClick={() => { setConfirmReset(false); onReset() }}
            >Ja</button>
            <button
              className='text-xs font-semibold text-neutral-500 dark:text-neutral-400 hover:underline'
              onClick={() => setConfirmReset(false)}
            >Nein</button>
          </span>
        ) : (
          <button className='text-xs font-semibold text-primary hover:underline' onClick={() => setConfirmReset(true)}>
            Reset
          </button>
        ))}
      >
        Spalten
      </SectionHead>
      <div className='px-3 pb-2'>
        <input
          type='text'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Spalte suchen…'
          className='w-full h-8 text-sm font-normal bg-neutral-100 dark:bg-neutral-800 rounded-lg px-2.5 focus:outline-none focus:ring-1 focus:ring-primary'
        />
      </div>
      <ul
        ref={listRef}
        className='max-h-72 overflow-auto flex flex-col gap-1 px-2 pb-2'
        onDragOver={(e) => { e.preventDefault(); dragYRef.current = e.clientY }}
        // Zeilen-Drops stoppen die Propagation, hier kommt nur "daneben" an:
        // NUR klar unterhalb der letzten Zeile heisst "ans Ende". Drops in
        // den Luecken zwischen den Zeilen oder im Padding (kein gruener
        // Balken sichtbar = mehrdeutig) aendern nichts.
        onDrop={(e) => {
          e.preventDefault()
          const items = listRef.current?.querySelectorAll('li[data-col-id]')
          const last = items?.[items.length - 1]
          if (last && e.clientY > last.getBoundingClientRect().bottom) {
            reorder(dragId, '__end')
          }
          endDrag()
        }}
      >
        {filteredColumns.map(([id, title]) => {
          const off = hiddenColumnIds.includes(id)
          return (
            <li
              key={id}
              data-col-id={id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move'
                // Firefox startet den Drag ohne setData nicht
                e.dataTransfer.setData('text/plain', id)
                setDragId(id)
                startAutoScroll()
                // Suche beim Anfassen aufloesen: gesucht wird zum Finden,
                // verschoben wird im vollen Kontext -- die komplette Liste
                // klappt auf und die gegriffene Zeile wird in Sicht gescrollt.
                // Verzoegert, weil DOM-Aenderungen waehrend des dragstart-
                // Events den Drag abbrechen (Chrome).
                if (search.trim()) {
                  setTimeout(() => {
                    setSearch('')
                    requestAnimationFrame(() => {
                      listRef.current
                        ?.querySelector(`[data-col-id="${CSS.escape(id)}"]`)
                        ?.scrollIntoView({ block: 'center' })
                    })
                  }, 0)
                }
              }}
              onDragEnd={endDrag}
              onDragOver={(e) => { e.preventDefault(); setOverId(id) }}
              onDragLeave={() => setOverId((o) => (o === id ? null : o))}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                reorder(dragId, id)
                endDrag()
              }}
              className={classNames(
                'flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800',
                off && 'opacity-40',
                dragId === id && 'opacity-30',
                // rounded-t-none: sonst folgt der border-top der Eckenrundung
                // und die Indikator-Linie wirkt gewoelbt statt gerade
                overId === id && dragId !== id && 'border-t-2 border-primary rounded-t-none'
              )}
            >
              <span className='shrink-0 leading-none cursor-grab active:cursor-grabbing text-neutral-400 dark:text-neutral-500'>
                <i className='bx bx-grid-vertical'></i>
              </span>
              <span className='flex-1 min-w-0 truncate text-sm font-medium'>{title}</span>
              <button
                type='button'
                className='shrink-0 leading-none text-base text-neutral-400 dark:text-neutral-500 hover:text-primary'
                title={off ? 'Einblenden' : 'Ausblenden'}
                onClick={() => {
                  setHiddenColumnIds((prev) => off
                    ? prev.filter((_id) => _id !== id)
                    : [...prev, id])
                }}
              >
                <i className={off ? 'bx bx-hide' : 'bx bx-show'}></i>
              </button>
            </li>
          )
        })}
        {filteredColumns.length === 0 && (
          <li className='py-3 text-center text-xs text-neutral-400 dark:text-neutral-500'>
            Keine Spalte gefunden
          </li>
        )}
      </ul>
    </>
  )
}
